import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass';
import { Button, Icon as IconSementic } from 'semantic-ui-react';

import { SplitScore, Number, Text, Link, SButton, Icon } from '../Base';
import { COLOR, FONT_SIZE, SPACE } from '../Theme';
import { isMed } from '../../Util/Subject';
import { getConfig } from '../../Util/Config';
import { getNumberVariant } from '../Base/Number';

import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow as BaseTableRow,
} from './TableComponents';

const { enableStudentRegistration } = getConfig();

const SORTING_SUBJECT_CODE = [
  '85',
  '71',
  '72',
  '73',
  '74',
  '75',
  '39',
  '89',
  '09',
  '19',
  '29',
  '49',
  '59',
  '69',
  '99',
  '00',
];

const compareString = (a, b) => {
  if (a > b) {
    return 1;
  } else if (a < b) {
    return -1;
  } else {
    return 0;
  }
};

const sortSubjects = items => {
  const results = [...items];
  return results.sort((a, b) => {
    const indexA = SORTING_SUBJECT_CODE.indexOf(a.subjectCode);
    const indexB = SORTING_SUBJECT_CODE.indexOf(b.subjectCode);
    const hasA = indexA !== -1;
    const hasB = indexB !== -1;
    if (hasA && hasB) {
      return indexA - indexB;
    } else if (hasA) {
      return -1;
    } else if (hasB) {
      return 1;
    } else {
      return compareString(a.subject, b.subject);
    }
  });
};

const TableRow = props => <BaseTableRow height="48px" {...props} />;

const ExamListTable = ({ rows, onExamClick, title, onManageExamClick, showManageButton }) => {
  const sortedRows = sortSubjects(rows);
  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={16}>
        {title && <Text fontSize={[FONT_SIZE.semilarge]}>{title}</Text>}
        {showManageButton && enableStudentRegistration && (
          <Button
            primary
            circular
            compact
            onClick={onManageExamClick}
            style={{ minWidth: '150px', height: '40px', display: 'inline-block' }}
          >
            <Text is="span" fontWeight="400" color="white" mr={8}>
              จัดการการสอบ
            </Text>
            <IconSementic name="plus" size="small" />
          </Button>
        )}
      </Flex>
      <Box style={{ overflowX: 'auto' }}>
        <Table minWidth={'700px'} padding={'checkbox'} border>
          <TableHead>
            <TableRow type={'head'} bgColor={COLOR.gridGrey}>
              <TableCell>วิชาที่สอบ</TableCell>
              <TableCell>คะแนนที่ได้/เต็ม</TableCell>
              <TableCell>คะแนนเฉลี่ย</TableCell>
              <TableCell>อันดับที่/ผู้เข้าสอบ</TableCell>
              <TableCell>เปอร์เซ็นไทล์</TableCell>
              <TableCell>ผลสอบและเฉลย</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, index) => {
              const { subjectCode } = row;
              return (
                <TableRow key={index}>
                  <TableCell color={COLOR.primary}>
                    <Flex alignItems={'center'}>
                      <img
                        src={row.subjectIcon}
                        alt={'subject-icon'}
                        width="32px"
                        style={{ marginRight: '12px', overflow: 'hidden' }}
                      />
                      <Link
                        onClick={onExamClick(row.examCardId, row)}
                        disabled={!row.released}
                        color={'primary'}
                        disabledColor={COLOR.primary}
                      >
                        {row.subject}
                      </Link>
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <SplitScore
                      score={row.score}
                      fullScore={row.fullScore}
                      variant={isMed(subjectCode) ? 'meds' : 'other'}
                      align="right"
                    />
                  </TableCell>
                  <TableCell textAlign="right">
                    <Number
                      value={row.averageScore}
                      variant={getNumberVariant(isMed(subjectCode) ? 'meds' : 'other')}
                    />
                  </TableCell>
                  <TableCell>
                    <SplitScore
                      score={row.ranking}
                      fullScore={row.testerCount}
                      variant="rank"
                      align="right"
                    />
                  </TableCell>
                  <TableCell color={COLOR.primary} textAlign="right">
                    <Number value={row.percentile} variant="twoDecimals" />
                  </TableCell>
                  <TableCell>
                    <SButton
                      primary={row.released}
                      onClick={onExamClick(row.examCardId, row)}
                      style={{ margin: 0, width: '140px' }}
                      disabled={!row.released}
                    >
                      {row.released ? 'ดูเพิ่มเติม' : `ประกาศ ${row.releaseDate || 'เร็วๆนี้'}`}
                      <Icon
                        name="arrow_forward_ios"
                        ml={SPACE.xsmall}
                        iconStyle={{ lineHeight: '0.9em' }}
                      />
                    </SButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

ExamListTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      subjectIcon: PropTypes.string,
      subject: PropTypes.string,
      score: PropTypes.number,
      fullScore: PropTypes.number,
      averageScore: PropTypes.number,
      ranking: PropTypes.number,
      testerCount: PropTypes.number,
      percentile: PropTypes.number,
      released: PropTypes.bool,
      releaseDate: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
  onExamClick: PropTypes.func,
};
ExamListTable.defaultProps = {
  rows: [],
  onExamClick: () => {},
};

export default ExamListTable;
